import { useSyncExternalStore, useCallback } from 'react';
import {
  subscribeTts, getSpeakingId, speakTts, stopTts,
  getTtsPaused, getTtsSeekable, getTtsLoading, getTtsElapsed, pauseResumeTts, seekTts,
} from '../lib/tts';

/**
 * Subscribe to the shared TTS engine. Returns the id currently speaking
 * (or null), a `toggle(id, text)` that speaks/stops, and — for OpenAI
 * playback (a real HTMLAudioElement) — ChatGPT-style transport controls:
 * `pauseResume()`, `seek(±sec)`, plus `paused`/`seekable` state (Alex #206).
 */
export function useTts() {
  const speakingId = useSyncExternalStore(subscribeTts, getSpeakingId, getSpeakingId);
  const paused = useSyncExternalStore(subscribeTts, getTtsPaused, getTtsPaused);
  const seekable = useSyncExternalStore(subscribeTts, getTtsSeekable, getTtsSeekable);
  const loading = useSyncExternalStore(subscribeTts, getTtsLoading, getTtsLoading);
  // Ticks every second while loading (see tts.ts load ticker) → the button can
  // show elapsed seconds so a long prose synthesis doesn't look hung.
  const loadingSecs = useSyncExternalStore(subscribeTts, getTtsElapsed, getTtsElapsed);

  const toggle = useCallback((id: string, text: string) => {
    if (getSpeakingId() === id) stopTts();
    else speakTts(id, text);
  }, []);

  return {
    speakingId, paused, seekable, loading, loadingSecs, toggle,
    stop: stopTts, pauseResume: pauseResumeTts, seek: seekTts,
  };
}
